import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import type { PlatformRole } from '../../model/types';

/** A role the selector offers; `assignable: false` lists it without letting it be picked. */
export type RoleSelectorOption = Pick<PlatformRole, 'id' | 'roleName'> & { assignable?: boolean };

export interface RoleSelectorProps {
  roleId: string;
  roles: RoleSelectorOption[];
  onRoleChange: (roleId: string) => void;
  disabled?: boolean;
  /** Overrides the default "Role: …" name, for hosts whose levels are not platform roles. */
  ariaLabel?: string;
}

export function RoleSelector({
  roleId,
  roles,
  onRoleChange,
  disabled,
  ariaLabel,
}: RoleSelectorProps) {
  const { t } = useTranslation('projects');

  const currentLabel =
    roles.find((r) => r.id === roleId)?.roleName ?? t('projectMember.roleSelector.noRole');
  const someRolesClosed = roles.some((r) => r.assignable === false);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 px-3 text-xs"
          disabled={disabled}
          aria-label={ariaLabel ?? t('projectMember.roleSelector.role', { role: currentLabel })}
        >
          {currentLabel}
          <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {roles.map((r) => (
          <DropdownMenuItem
            key={r.id}
            disabled={r.assignable === false}
            onSelect={() => onRoleChange(r.id)}
            className="flex items-center justify-between gap-3"
          >
            {/* A disabled item takes no hover or focus, so the reason sits in it, not in a tooltip. */}
            <span className="flex flex-col">
              {r.roleName}
              {r.assignable === false && (
                <span className="text-xs text-text-secondary">
                  {t('projectMember.roleSelector.includesPermissionsYouLack')}
                </span>
              )}
            </span>
            {r.id === roleId && <Check className="size-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        {someRolesClosed && (
          <>
            <DropdownMenuSeparator />
            <p className="max-w-64 px-2 py-1.5 text-xs text-text-secondary">
              {t('projectMember.roleSelector.organizationAdminCanAssign')}
            </p>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
